import axios from "axios";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ExcelColumnConfig } from "fe-shared/src/types/common";
import { DataGridRequestParams } from "fe-shared-transactions/src/types";
import { HistoryLogsResponse } from "fe-shared-transactions/src/hooks/useTransactionsExport";
import {
  formatTransactionNumber,
  formatRecord,
} from "fe-shared-transactions/src/helpers/format";

import {
  GridInventoryAdjustmentExport,
  GridInventoryAdjustmentList,
} from "../types/inventoryAdjustments";
import { getFormattedDate } from "../components/TransactionsDataGrid/modules/config";
import { convertToAdditionOrSubtractionValue } from "helpers/inventoryAdjustmentsRequestConverter";
import { getFullAddress } from "fe-shared-transactions/src/helpers/address";

const limit = 500;

export const getInventoryAdjustmentExport = async (
  requestParams: DataGridRequestParams
) => {
  let skip = 0;
  let total = 0;
  const inventoryAdjustments: GridInventoryAdjustmentExport[] = [];

  do {
    // eslint-disable-next-line no-await-in-loop
    const { data } = await axios.post<
      GridInventoryAdjustmentList<GridInventoryAdjustmentExport>
    >("/getExportableOverallAdjustments", {
      ...requestParams,
      skip,
      limit,
    });

    total = data.total;
    skip += limit;
    inventoryAdjustments.push(...data.overallAdjustments);
  } while (skip < total);

  const transactionNumbers = inventoryAdjustments.map(
    (t) => t.overallAdjustmentNumber
  );

  const historyLogsByInvoiceNumber = await axios
    .post<HistoryLogsResponse>("history/fetchForExport", {
      objectIds: transactionNumbers,
      entityType: "overallAdjustment",
    })
    .then((res) => res.data);

  return inventoryAdjustments.map((i) => ({
    ...i,
    historyLogs: historyLogsByInvoiceNumber[i.overallAdjustmentNumber],
  }));
};

const useInventoryAdjustmentsExport = (
  requestParams: DataGridRequestParams
) => {
  const { t } = useTranslation();
  const getDataRows = () => getInventoryAdjustmentExport(requestParams);

  const columnConfig = useMemo(
    (): ExcelColumnConfig<GridInventoryAdjustmentExport>[] => [
      {
        width: 15,
        mappedKey: t(
          "inventoryAdjustment.grid.export.cols.inventoryAdjustmentNumber"
        ),
        getOriginalValue: (i) =>
          formatTransactionNumber(i.overallAdjustmentNumber, ""),
      },
      {
        width: 15,
        mappedKey: t(
          "inventoryAdjustment.grid.export.cols.participantRegNumber"
        ),
        getOriginalValue: (i) => i.participantRegNumber,
      },
      {
        width: 25,
        mappedKey: t("inventoryAdjustment.grid.export.cols.participantName"),
        getOriginalValue: (i) => i.participantDisplayName,
      },
      {
        width: 15,
        mappedKey: t("inventoryAdjustment.grid.export.cols.completedDate"),
        getOriginalValue: (i) => getFormattedDate(i.completedDate),
      },
      {
        width: 15,
        mappedKey: t("inventoryAdjustment.grid.export.cols.status"),
        getOriginalValue: (i) =>
          t(`inventoryAdjustment.statuses.${i.statusCode}`),
      },
      {
        width: 15,
        mappedKey: t("inventoryAdjustment.grid.export.cols.createdBy"),
        getOriginalValue: (i) => i.createdBy,
      },
      {
        width: 15,
        mappedKey: t("inventoryAdjustment.grid.export.cols.approvedBy"),
        getOriginalValue: (i) => i.approvedBy,
      },
      {
        width: 15,
        mappedKey: t("inventoryAdjustment.grid.export.cols.approvedDate"),
        getOriginalValue: (i) => i.approvedDate ? getFormattedDate(i.approvedDate) : "",
      },
      {
        width: 15,
        mappedKey: t("inventoryAdjustment.grid.export.cols.adjustmentDate"),
        getOriginalValue: (i) => getFormattedDate(i.adjustmentDate),
      },
      {
        width: 15,
        mappedKey: t("inventoryAdjustment.grid.export.cols.regulatoryYear"),
        getOriginalValue: (i) => i.regulatoryYear,
      },
      {
        width: 15,
        mappedKey: t("inventoryAdjustment.grid.export.cols.scaleTicketNumber"),
        getOriginalValue: (i) => i.scaleTicketNumber,
      },
      {
        width: 40,
        mappedKey: t("inventoryAdjustment.grid.export.cols.storageYard"),
        getOriginalValue: (i) => getFullAddress(i.address),
      },
      {
        width: 30,
        mappedKey: t("inventoryAdjustment.grid.export.cols.materialType"),
        getOriginalValue: (i) => i.materialType,
      },
      {
        width: 30,
        mappedKey: t("inventoryAdjustment.grid.export.cols.tireType"),
        getOriginalValue: (i) => i.tireType,
      },
      {
        width: 15,
        mappedKey: t("inventoryAdjustment.grid.export.cols.transactionNumber"),
        getOriginalValue: (i) => i.transactionNumber,
      },
      {
        width: 15,
        mappedKey: t("inventoryAdjustment.grid.export.cols.addorSub"),
        getOriginalValue: (i) => i.addorSub,
      },
      {
        width: 15,
        mappedKey: t("inventoryAdjustment.grid.export.cols.quantity"),
        getOriginalValue: (i) => convertToAdditionOrSubtractionValue(i.quantity, i.addorSub),
      },
      {
        width: 15,
        mappedKey: t("inventoryAdjustment.grid.export.cols.weight"),
        getOriginalValue: (i) => convertToAdditionOrSubtractionValue(i.weight, i.addorSub),
      },
      {
        width: 15,
        mappedKey: t("inventoryAdjustment.grid.export.cols.comment"),
        getOriginalValue: (i) => i.comment,
      },
      {
        width: 40,
        mappedKey: t("inventoryAdjustment.grid.export.cols.overallAdjustmentNotes"),
        getOriginalValue: (i: GridInventoryAdjustmentExport) =>
          (i.overallAdjustmentNotes || []).map(x => x).join("\n")
      },
      {
        width: 15,
        mappedKey: t("inventoryAdjustment.grid.export.cols.supportDocuments"),
        getOriginalValue: (i) => i.supportDocuments,
      },
      {
        width: 30,
        mappedKey: t("inventoryAdjustment.grid.export.cols.notes"),
        getOriginalValue: (i: GridInventoryAdjustmentExport) =>
          (i.notes || []).map(formatRecord).join("\n")
      },
      {
        width: 20,
        mappedKey: t("inventoryAdjustment.grid.export.cols.historyLogs"),
        getOriginalValue: (i: GridInventoryAdjustmentExport) =>
          (i.historyLogs || []).map(formatRecord).join("\n")
      },
    ],
    [t]
  );

  return {
    getDataRows,
    columnConfig,
  };
};

export default useInventoryAdjustmentsExport;
